// Copyright The OpenTelemetry Authors
// SPDX-License-Identifier: Apache-2.0

import { createContext, useContext, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import ApiGateway from '../gateways/Api.gateway';
import { recommendationsQueryOptions } from '../gateways/recommendationsQuery';
import { Ad, Money, Product } from '../protos/demo';
import { useCurrency } from './Currency.provider';

interface IContext {
  recommendedProductList: Product[];
  adList: Ad[];
}

export const Context = createContext<IContext>({
  recommendedProductList: [],
  adList: [],
});

interface IProps {
  children: React.ReactNode;
  productIds: string[];
  contextKeys: string[];
  // When true, AdProvider does not auto-fetch recommendations on mount. The
  // owning page is responsible for dispatching the fetch (via
  // queryClient.prefetchQuery with recommendationsQueryOptions) immediately
  // after its Faro user action, so the request is attributed to that action.
  // AdProvider still reads/renders the result from the shared query cache.
  deferRecommendationsFetch?: boolean;
}

export const useAd = () => useContext(Context);

const AdProvider = ({ children, productIds, contextKeys, deferRecommendationsFetch = false }: IProps) => {
  const { selectedCurrency } = useCurrency();
  const { data: adList = [] } = useQuery({
    queryKey: ['ads', contextKeys],
    queryFn: () => {
      if (contextKeys.length === 0) {
        return Promise.resolve([]);
      } else {
        return ApiGateway.listAds(contextKeys);
      }
    },
    refetchOnWindowFocus: false
  });
  const { data: recommendedProductList = [] } = useQuery({
    ...recommendationsQueryOptions(productIds, selectedCurrency),
    // When deferred, the owning page drives the fetch from inside its user
    // action; AdProvider only subscribes to the resulting cache entry.
    enabled: !deferRecommendationsFetch,
    refetchOnWindowFocus: false
  });

  const value = useMemo(
    () => ({
      adList,
      recommendedProductList,
    }),
    [adList, recommendedProductList]
  );

  return <Context.Provider value={value}>{children}</Context.Provider>;
};

export default AdProvider;
